import type { Page } from '../../app/feature';
import { esc, q } from '../../shared/dom';
import { t } from '../../shared/i18n';
import { dateTime, fmt, money } from '../../shared/format';
import { ApiError } from '../../shared/api/http';
import { getState } from '../../shared/store/store';
import { hydrateFromApi } from '../../shared/store/sync';
import { toast } from '../../shared/ui/toast';
import { href } from '../../app/router';
import { isAdmin } from '../../shared/session/session';
import { usersApi } from '../users/users.api';
import { hostsApi, type HostDetail } from './hosts.api';

let hostId: string | null = null;
let host: HostDetail | null = null;

/**
 * Ficha do influenciador: sorteio atual (e atribuição), histórico de sorteios, prêmios
 * entregues e ações de acesso. Suporte vê; só admin altera.
 */
export const hostDetail: Page = {
  id: 'influencer',
  pattern: '/influencers/:id',
  roles: ['ADMIN', 'SUPPORT'],
  navParent: 'influencers',

  template: () => `
<div class="crumbs"><a class="auth-link" href="${href('/influencers')}" data-i18n="hs.back">← Influencers</a></div>
<div id="hd-missing" class="panel" hidden><div class="empty" data-i18n="hs.notFound">Este influencer ya no existe.</div></div>
<div id="hd-body">
  <div class="panel hero">
    <div class="hero-main">
      <div class="inline" style="gap:8px; margin-bottom:8px"><span class="chip" id="hd-status"></span><span class="chip chip-dim" id="hd-handle" hidden></span></div>
      <h2 id="hd-name" class="hero-title">—</h2>
      <p id="hd-meta" class="muted"></p>
    </div>
    <div class="hero-actions" id="hd-actions">
      <button class="btn" id="hd-newpass">🔑 <span data-i18n="users.newPass">Nueva contraseña</span></button>
      <button class="btn btn-ghost" id="hd-toggle"></button>
    </div>
    <div class="stats hero-stats">
      <div style="--accent:var(--orange)"><b id="hd-k-events">0</b><span data-i18n="hs.thEvents">Sorteos</span><small data-i18n="hs.kEventsSub">en los que participó</small></div>
      <div style="--accent:var(--gold)"><b id="hd-k-draws">0</b><span data-i18n="hs.thDraws">Premios</span><small data-i18n="hs.kDrawsSub2">sorteados al vivo</small></div>
      <div style="--accent:var(--green)"><b id="hd-k-prizes">$0</b><span data-i18n="hs.thPrizes">Valor entregado</span><small data-i18n="hs.kPrizesSub">suma de los premios sorteados</small></div>
      <div style="--accent:var(--line-2)"><b id="hd-k-login" style="font-size:16px; margin-top:4px">—</b><span data-i18n="users.lastLogin">Último acceso</span><small id="hd-k-since">—</small></div>
    </div>
  </div>

  <div class="cols" style="margin-top:18px">
    <div class="panel span-5">
      <div class="panel-head"><h3 data-i18n="hs.currentTitle">Sorteo actual</h3></div>
      <p class="hint" data-i18n="hs.currentHint">Es el único sorteo que este influencer ve al entrar. Cambiarlo aquí vale de inmediato.</p>
      <div id="hd-current" style="margin:12px 0"></div>
      <div class="field" id="hd-assign-field">
        <label for="hd-assign" data-i18n="ev.assigned">Sorteo asignado</label>
        <select id="hd-assign"></select>
      </div>
    </div>

    <div class="panel span-7">
      <div class="panel-head"><h3 data-i18n="hs.historyTitle">Sorteos en los que participó</h3></div>
      <p class="hint" data-i18n="hs.historyHint">Cada vez que un admin lo asigna a un sorteo queda registrado, y también cada premio que sortea en la live.</p>
      <div class="tw tw-flat" style="margin-top:10px">
        <table id="hd-events">
          <thead><tr><th data-i18n="ev.name2">Sorteo</th><th data-i18n="ev.when">Fecha</th><th data-i18n="hs.thDraws">Premios</th><th data-i18n="hs.thPrizes">Valor</th><th data-i18n="th.status">Estado</th></tr></thead>
          <tbody></tbody>
        </table>
      </div>
    </div>

    <div class="panel span-12">
      <div class="panel-head"><h3 data-i18n="hs.drawsTitle">Premios que sorteó</h3></div>
      <div class="tw tw-flat">
        <table id="hd-draws">
          <thead><tr><th data-i18n="th.when">Fecha</th><th data-i18n="ev.name2">Sorteo</th><th data-i18n="th.prize">Premio</th><th data-i18n="th.winner">Ganador</th><th data-i18n="th.number">Número</th></tr></thead>
          <tbody></tbody>
        </table>
      </div>
    </div>
  </div>
</div>`,

  mount() {
    q<HTMLSelectElement>('#hd-assign').onchange = async () => {
      if (!host) return;
      const value = q<HTMLSelectElement>('#hd-assign').value || null;
      await run(() => usersApi.update(host!.id, { eventId: value }));
    };
    q('#hd-toggle').onclick = async () => {
      if (!host) return;
      if (host.active && !confirm(t('users.block') + `: ${host.name}?`)) return;
      await run(() => usersApi.update(host!.id, { active: !host!.active }));
    };
    q('#hd-newpass').onclick = async () => {
      if (!host) return;
      if (!confirm(t('users.confirmNewPass').replace('{name}', host.name))) return;
      const password = generatePassword();
      const h = host;
      await run(async () => {
        await usersApi.update(h.id, { password });
        window.prompt(t('hs.passHint').replace('{email}', h.email), password);
      });
    };
  },

  render(params) {
    const id = params.id ?? null;
    if (id !== hostId) { hostId = id; host = null; paint(); }
    void reload();
  }
};

async function run(action: () => Promise<unknown>): Promise<void> {
  try { await action(); toast(t('msg.saved')); await reload(); void hydrateFromApi(); }
  catch (err) { toast(err instanceof ApiError ? err.message : t('auth.errGeneric')); }
}

async function reload(): Promise<void> {
  if (!hostId) return;
  try { host = await hostsApi.get(hostId); }
  catch (err) {
    if (err instanceof ApiError && err.status === 404) { host = null; paint(true); return; }
    toast(err instanceof ApiError ? err.message : t('auth.errGeneric'));
  }
  paint();
}

function paint(missing = false): void {
  q('#hd-missing').hidden = !missing;
  q('#hd-body').hidden = missing;
  q('#hd-actions').hidden = !isAdmin();
  q('#hd-assign-field').hidden = !isAdmin();
  if (!host) { q('#hd-name').textContent = '…'; return; }
  const h = host;

  q('#hd-name').textContent = h.name;
  q('#hd-status').textContent = h.active ? t('ev.on') : t('users.blocked');
  q('#hd-status').className = `chip ${h.active ? 'chip-green' : 'chip-dim'}`;
  q('#hd-handle').hidden = !h.handle;
  q('#hd-handle').textContent = h.handle ?? '';
  q('#hd-meta').textContent = `${h.email} · ${t('pl.since')} ${dateTime(Date.parse(h.createdAt))}${h.expiresAt ? ` · ${t('hs.expires')} ${dateTime(Date.parse(h.expiresAt))}` : ''}`;
  q('#hd-toggle').textContent = h.active ? `🚫 ${t('users.block')}` : `✔ ${t('users.unblock')}`;

  q('#hd-k-events').textContent = fmt(h.eventsHosted);
  q('#hd-k-draws').textContent = fmt(h.drawsCount);
  q('#hd-k-prizes').textContent = money(h.prizesTotal);
  q('#hd-k-login').textContent = h.lastLoginAt ? dateTime(Date.parse(h.lastLoginAt)) : t('hs.neverLogged');
  q('#hd-k-since').textContent = `${t('pl.since')} ${dateTime(Date.parse(h.createdAt))}`;

  q('#hd-current').innerHTML = h.currentEvent
    ? `<a class="ev-mini" href="${href('/sorteos/' + h.currentEvent.id)}">
         <span class="chip ${h.currentEvent.tier === 'MENSUAL' ? 'chip-gold' : 'chip-orange'}">${esc(h.currentEvent.tier === 'MENSUAL' ? t('tier.monthly') : t('tier.weekly'))}</span>
         <b>${esc(h.currentEvent.name)}</b>
         <span class="muted">📅 ${esc(h.currentEvent.scheduledAt ? dateTime(Date.parse(h.currentEvent.scheduledAt)) : t('ov.noDate'))} · ${esc(t('evst.' + h.currentEvent.status.toLowerCase()))}</span>
       </a>`
    : `<div class="empty" style="padding:16px">${esc(t('hs.noEventLong'))}</div>`;

  const sel = q<HTMLSelectElement>('#hd-assign');
  sel.innerHTML = `<option value="">${esc(t('ev.unassigned'))}</option>` +
    getState().events.map(e => `<option value="${e.id}" ${h.currentEvent?.id === e.id ? 'selected' : ''}>${esc(e.name)}</option>`).join('');

  q('#hd-events tbody').innerHTML = h.events.map(e => `<tr>
    <td><a class="auth-link" href="${href('/sorteos/' + e.id)}"><b>${esc(e.name)}</b></a>${e.isCurrent ? ` <span class="chip chip-orange">${esc(t('hs.current'))}</span>` : ''}<div class="muted" style="font-size:11.5px">${esc(e.tier === 'MENSUAL' ? t('tier.monthly') : t('tier.weekly'))}</div></td>
    <td class="tnum">${e.scheduledAt ? dateTime(Date.parse(e.scheduledAt)) : '—'}</td>
    <td class="tnum">${fmt(e.drawsCount)}</td>
    <td class="tnum">${money(e.prizesTotal)}</td>
    <td><span class="chip ${e.status === 'LIVE' ? 'chip-green' : e.status === 'SCHEDULED' ? 'chip-orange' : 'chip-dim'}">${esc(t('evst.' + e.status.toLowerCase()))}</span></td>
  </tr>`).join('') || `<tr><td colspan="5" class="empty">${esc(t('hs.noHistory'))}</td></tr>`;

  q('#hd-draws tbody').innerHTML = h.draws.map(d => `<tr>
    <td class="muted tnum">${dateTime(Date.parse(d.at))}</td>
    <td>${esc(d.eventName)}</td>
    <td>${d.prizeEmoji ?? '🎁'} ${esc(d.prizeName)} <span class="muted">· ${money(d.prizeValue)}</span></td>
    <td>${esc(d.playerName)}</td>
    <td class="t-num">${d.number}</td>
  </tr>`).join('') || `<tr><td colspan="5" class="empty">${esc(t('hs.noDraws'))}</td></tr>`;
}

/** Senha temporária legível (sem caracteres ambíguos). */
function generatePassword(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  const bytes = crypto.getRandomValues(new Uint8Array(12));
  return Array.from(bytes, b => chars[b % chars.length]).join('');
}
